import React, { useState } from 'react';
import SectionHeader from '../../../common/marketing/SectionHeader';
import { trackEvent } from '../../../../utils/firebase';
import { openQuoteChat } from '../../../../utils/site';

const MAX_STRINGS = 50;

const OrbitStarLengthCalculator = ({ calculator, variants, onChoose }) => {
    const [length, setLength] = useState(String(calculator.defaultLength));
    const [unitId, setUnitId] = useState(calculator.defaultUnit);

    const unit = calculator.units.find((item) => item.id === unitId);
    const lengthMetres = parseFloat(length) * unit.metres;
    const isValid = lengthMetres > 0 && Number.isFinite(lengthMetres);
    const priced = variants.models.filter((model) => model.price && model.lengthMetres);
    const isBulk = isValid && priced.some((model) => Math.ceil(lengthMetres / model.lengthMetres) > MAX_STRINGS);

    const options = isValid && !isBulk
        ? priced.map((model) => {
            const count = Math.ceil(lengthMetres / model.lengthMetres);
            return { model, count, total: count * model.price, covers: count * model.lengthMetres };
        })
        : [];
    const best = options.reduce((winner, option) => (
        !winner || option.total < winner.total || (option.total === winner.total && option.count < winner.count) ? option : winner
    ), null);

    const choose = (option) => {
        trackEvent('calculator_choose', {
            length_m: Math.round(lengthMetres * 10) / 10,
            item_name: `Orbit Star ${option.model.label}`,
            quantity: option.count
        });
        onChoose(option.model.id);
    };

    return (
        <section id="how-many-lights" className="orbit-star-calculator">
            <div className="container">
                <SectionHeader
                    wrapperClassName="orbit-star-section-header"
                    subtitleClassName="orbit-star-section-subtitle"
                    titleClassName="orbit-star-section-title"
                    descriptionClassName="orbit-star-calculator-intro"
                    subtitle={calculator.subtitle}
                    title={calculator.title}
                    description={calculator.description}
                />

                <div className="orbit-star-calculator-card">
                    <div className="orbit-star-calculator-input">
                        <label htmlFor="orbit-star-length">{calculator.inputLabel}</label>
                        <div className="orbit-star-calculator-field">
                            <input
                                id="orbit-star-length"
                                type="number"
                                inputMode="decimal"
                                min="1"
                                value={length}
                                onChange={(event) => setLength(event.target.value)}
                            />
                            <div className="orbit-star-calculator-units" role="group" aria-label={calculator.inputLabel}>
                                {calculator.units.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className={item.id === unitId ? 'is-active' : ''}
                                        aria-pressed={item.id === unitId}
                                        onClick={() => setUnitId(item.id)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {!isValid ? <p className="orbit-star-calculator-message">{calculator.emptyNote}</p> : null}
                    {isBulk ? (
                        <div className="orbit-star-calculator-message">
                            <p>{calculator.bulkNote}</p>
                            <button type="button" className="orbit-star-calculator-btn" onClick={openQuoteChat}>
                                {calculator.bulkButtonLabel}
                            </button>
                        </div>
                    ) : null}
                    <div className="orbit-star-calculator-results" aria-live="polite">
                        {options.map((option) => (
                            <div
                                key={option.model.id}
                                className={`orbit-star-calculator-option${option === best ? ' is-best' : ''}`}
                            >
                                {option === best ? <span className="orbit-star-calculator-best">{calculator.bestLabel}</span> : null}
                                <div className="orbit-star-calculator-count">
                                    {option.count} × {option.model.label}
                                    <span>
                                        {option.model.sublabel} {option.count === 1 ? calculator.stringLabel : calculator.stringsLabel}
                                    </span>
                                </div>
                                <div className="orbit-star-calculator-covers">
                                    {calculator.coverTemplate.replace('{length}', option.covers)}
                                </div>
                                <div className="orbit-star-calculator-price">
                                    {variants.currencySymbol}{option.total} {calculator.totalLabel}
                                    <span>{variants.currencySymbol}{option.model.price} {calculator.eachLabel}</span>
                                </div>
                                {option.model.amazonPacks.includes(option.count) ? (
                                    <div className="orbit-star-calculator-pack">
                                        {calculator.packTemplate.replace('{count}', option.count)}
                                    </div>
                                ) : null}
                                <button type="button" className="orbit-star-calculator-btn" onClick={() => choose(option)}>
                                    {calculator.buttonLabel}
                                </button>
                            </div>
                        ))}
                    </div>
                    <p className="orbit-star-calculator-note">{calculator.note}</p>
                </div>
            </div>
        </section>
    );
};

export default OrbitStarLengthCalculator;
